<!--
  @component
  Generates an HTML y-axis.
 -->
<script>
	import { getContext } from "svelte";
	import { tweened } from "svelte/motion";
	import { fade, blur, fly, slide, scale } from "svelte/transition";
	import { animationParameters } from "../constants";

	const { padding, xRange, yScale } = getContext("LayerCake");

	/** @type {Boolean} [gridlines=true] - Extend lines from the ticks into the chart space */
	export let gridlines = true;

	/** @type {Boolean} [tickMarks=false] - Show a vertical mark for each tick. */
	export let tickMarks = false;

	/** @type {Boolean} [baseline=false] – Show a solid line at the bottom. */
	export let baseline = false;

	export let ticksPositionX = "left"; // can be "left" or "right"
	export let ticksPositionY = "above"; // can be "above" or "on-gridline"

	/** @type {Function} [formatTick=d => d] - A function that passes the current tick value and expects a nicely formatted value in return. */
	export let formatTick = (d, i, isLast) => d;

	/** @type {Number|Array|Function} [ticks=4] - If this is a number, it passes that along to the [d3Scale.ticks](https://github.com/d3/d3-scale) function. If this is an array, hardcodes the ticks to those values. If it's a function, passes along the default tick values and expects an array of tick values in return. */
	export let ticks = 4;

	/** @type {Number} [xTick=-4] - How far over to position the text marker. */
	export let xTick = -4;

	/** @type {Number} [yTick=-1] - How far up and down to position the text marker. */
	export let yTick = -1;

	$: isBandwidth = typeof $yScale.bandwidth === "function";

	// $: tickVals = Array.isArray(ticks)
	// 	? ticks
	// 	: isBandwidth
	// 	? $yScale.domain()
	// 	: typeof ticks === "function"
	// 	? ticks($yScale.ticks())
	// 	: $yScale.ticks(ticks);

	$: tickVals = ticks;

	$: ticksTop = tickVals.map((tick) => $yScale(tick) + (isBandwidth ? $yScale.bandwidth() / 2 : 0));
	const tTicksTop = tweened(ticksTop, animationParameters);
	$: tTicksTop.set(ticksTop);

	$: isVisible = (tick, i) => $tTicksTop[i] >= 0 && $tTicksTop[i] <= 100;
	$: tickValsVisible = tickVals.filter((tick, i) => isVisible(tick, i));
</script>

<div class="axis y-axis" style="transform:translate(-{$padding.left}px, 0)">
	{#each tickVals as tick, i}
		{@const top = $tTicksTop[i]}
		{@const isLast = tickValsVisible.indexOf(tick) === tickValsVisible.length - 1}
		{#if top >= 0 && top <= 100 && !(tickValsVisible.length > 6 && i % 2 === 1 && tick !== 0)}
      <div class="tick tick-{i}" style="top:0;left:{$xRange[0]}%;transform:translateY({top}%)">
				{#if gridlines !== false}
					<div
						class="gridline"
						style="top:0;left:{isBandwidth || ticksPositionY === 'on-gridline'
							? $padding.left
							: 0}px;right:-{$padding.left + $padding.right}px;"
					/>
				{/if}
				{#if baseline !== false && i === 0}
					<div
						class="gridline baseline"
						style="top:0;left:{isBandwidth ? $padding.left : 0};right:-{$padding.left +
							$padding.right}px;"
					/>
				{/if}
				{#if tickMarks === true}
					<div
						class="tick-mark"
						style="top:0;left:{isBandwidth ? $padding.left - 6 : 0}px;width:6px;"
					/>
				{/if}
				<div
					class="text"
					style:padding-right="{isLast ? 3 : 0}px"
					style="
						top:{yTick}px;
						left:{isBandwidth
						? $padding.left + xTick - 4
						: ticksPositionX === 'left'
						? $xRange[0]
						: $xRange[1]}%;
						transform: translate({isBandwidth
						? '-100%'
						: ticksPositionX === 'left'
						? '0%'
						: '-100%'}, {ticksPositionY === 'on-gridline'
						? -50
						: isBandwidth
						? -50 - Math.floor($yScale.bandwidth() / -2)
						: '-100'}%);
					"
				>
					{@html formatTick(tick, i, isLast)}
				</div>
			</div>
		{/if}
	{/each}
</div>

<style lang="scss">
	@import "../main.scss";

	.axis,
	.tick,
	.tick-mark,
	.gridline,
	.baseline,
	.text {
		position: absolute;
	}
	.axis {
		width: 100%;
		height: 100%;
	}
	.tick {
    width: 100%;
    height: 100%;
    pointer-events: none;
	}

	.gridline {
		border-top: 1px solid #c0c0c0;
		opacity: 0.5;
	}
	.tick-mark {
		border-top: 1px solid #c0c0c0;
		opacity: 0.5;
	}

	.baseline.gridline {
		border-top-style: solid;
	}

	.tick .text {
		color: $black;
    text-shadow: $white 2px 0px 0px, $white 1.75517px 0.958851px 0px, $white 1.0806px 1.68294px 0px, $white 0.141474px 1.99499px 0px, $white -0.832294px 1.81859px 0px, $white -1.60229px 1.19694px 0px, $white -1.97999px 0.28224px 0px, $white -1.87291px -0.701566px 0px, $white -1.30729px -1.51361px 0px, $white -0.421592px -1.95506px 0px, $white 0.567324px -1.91785px 0px, $white 1.41734px -1.41108px 0px, $white 1.92034px -0.558831px 0px;
    width: 200px;
    pointer-events: none;
    text-align: left;
	}
</style>
